<template>
    <div class="admin-booking-form container-fluid px-4">
        <!-- Stepper/Progress Bar -->
        <b-progress height="18px" :max="5" class="mb-4">
            <b-progress-bar :value="step" variant="primary">
                Step {{ step }} / 5
            </b-progress-bar>
        </b-progress>

        <!-- Step 1: Guest & Booking Details -->
        <b-card class="mb-4" header="1. Guest & Booking Details" header-bg-variant="light">
            <b-row>
                <b-col md="6">
                            <b-form-group label="First Name*">
            <b-form-input v-model="formFields.first_name" :state="validateField('first_name')"
                @blur="validateField('first_name')" />
            <b-form-invalid-feedback>
                {{ errors.first_name || 'First name is required (min 2 characters)' }}
            </b-form-invalid-feedback>
        </b-form-group>
                </b-col>

                <b-col md="6">
                    <b-form-group label="Last Name*">
                        <b-form-input v-model="formFields.last_name" :state="validateField('last_name')"
                            @blur="validateField('last_name')" />
                        <b-form-invalid-feedback>
                            {{ errors.last_name || 'Last name is required (min 2 characters)' }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </b-col>

                <b-col md="6">
                    <b-form-group label="Email*">
                        <b-form-input v-model="formFields.email" type="email" :state="validateField('email')"
                            @blur="validateField('email')" />
                        <b-form-invalid-feedback>
                            {{ errors.email || 'Valid email is required' }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </b-col>

                <b-col md="6">
                    <b-form-group label="Phone*">
                        <vue-tel-input v-model="formFields.phone" mode="international" :inputOptions="{
                            placeholder: 'Enter phone number',
                            type: 'tel',
                            maxlength: 15
                        }" :class="{ 'is-invalid': errors.phone }" @input="handlePhoneInput"
                            @validate="handlePhoneValidate" @blur="validateField('phone')"></vue-tel-input>
                        <small v-if="errors.phone" class="text-danger">
                            {{ errors.phone }}
                        </small>
                    </b-form-group>
                </b-col>

                <b-col md="4">
                    <b-form-group label="Hotel*">
                        <b-form-select v-model="formFields.hotel_id" :options="hotelOptions"
                            :state="validateField('hotel_id')" @change="validateField('hotel_id')" />
                        <b-form-invalid-feedback>
                            {{ errors.hotel_id || 'Hotel selection is required' }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </b-col>

                <b-col md="8">
                    <b-form-group label="Stay Dates*">
                        <flat-pickr v-model="dateRange" :config="dateOptions" class="form-control"
                            :class="{ 'is-invalid': errors.dateRange }"
                            placeholder="Select check-in and check-out dates" @on-change="validateField('dateRange')" />
                        <small v-if="errors.dateRange" class="text-danger">
                            {{ errors.dateRange }}
                        </small>
                    </b-form-group>
                </b-col>

                <b-col md="6">
                    <b-form-group label="Number of Adults*">
                        <b-form-input v-model="formFields.num_of_adults" type="number" min="1"
                            :state="validateField('num_of_adults')" @blur="validateField('num_of_adults')" />
                        <b-form-invalid-feedback>
                            {{ errors.num_of_adults || 'At least 1 adult is required' }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </b-col>

                <b-col md="6">
                    <b-form-group label="Number of Children">
                        <b-form-input v-model="formFields.num_of_child" type="number" min="0"
                            :state="validateField('num_of_child')" @blur="validateField('num_of_child')" />
                        <b-form-invalid-feedback>
                            {{ errors.num_of_child || 'Must be 0 or more' }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </b-col>

                <b-col md="12">
                    <b-form-group label="Special Requests">
                        <b-form-textarea v-model="formFields.special_request" />
                    </b-form-group>
                </b-col>
            </b-row>
        </b-card>


        <!-- Step 2: Room Type Selection -->
        <b-card v-if="step >= 2" class="mb-4" header="2. Select Room Types" header-bg-variant="primary" header-text-variant="white">
            <div v-if="roomTypeOptions.length">
                <div v-if="errors.roomTypes" class="alert alert-danger mb-3">
                    <b-icon icon="exclamation-triangle" class="me-2"></b-icon>
                    {{ errors.roomTypes }}
                </div>
                <div class="row">
                    <div v-for="rt in roomTypeOptions" :key="rt.value" class="col-md-4 mb-4">
                        <div class="enhanced-room-type-card" :class="{
                            'selected': selectedRoomTypes.includes(rt.value),
                        }">
                            <div class="room-type-image">
                                <img v-if="getRoomTypeImage(rt)" :src="getRoomTypeImage(rt)" alt="Room Type"
                                    class="room-type-main-image" />
                                <div v-else class="room-type-placeholder">
                                    <b-icon icon="image" font-scale="3" class="text-muted"></b-icon>
                                </div>
                                <div class="room-type-overlay">
                                    <div class="price-badge">
                                        <span class="price-amount">{{ formatCurrency(rt.price) }}</span>
                                        <span class="price-period">per night</span>
                                    </div>
                                </div>
                            </div>
                            <div class="room-type-content">
                                <div class="room-type-header">
                                    <h5 class="room-type-title">{{ rt.text }}</h5>
                                    <div class="room-type-meta">
                                        <span class="capacity-badge">
                                            <b-icon icon="people" class="me-1"></b-icon>
                                            {{ rt.capacity }} guests
                                        </span>
                                        <span class="availability-badge">
                                            <b-icon icon="door-open" class="me-1"></b-icon>
                                            {{ rt.available_rooms }} available
                                        </span>
                                    </div>
                                </div>
                                
                                <div class="room-type-details">
                                    <div v-if="rt.bed_type" class="detail-item">
                                        <b-icon icon="bed" class="me-2 text-info"></b-icon>
                                        <span>{{ rt.bed_type }} bed</span>
                                    </div>
                                    <div v-if="rt.description" class="detail-item description-text">
                                        <b-icon icon="info-circle" class="me-2 text-warning"></b-icon>
                                        <span>{{ rt.description }}</span>
                                    </div>
                                </div>

                                <div class="room-type-actions">
                                    <b-button 
                                        :variant="selectedRoomTypes.includes(rt.value) ? 'success' : 'primary'"
                                        size="sm" 
                                        class="w-100 mb-2"
                                        @click="toggleRoomTypeSelection(rt.value)"
                                        :disabled="!rt.available_rooms">
                                        <b-icon :icon="selectedRoomTypes.includes(rt.value) ? 'check-circle' : 'plus-circle'" class="me-2"></b-icon>
                                        {{ selectedRoomTypes.includes(rt.value) ? 'Selected' : 'Select Room Type' }}
                                    </b-button>
                                    
                                    <b-button 
                                        v-if="selectedRoomTypes.includes(rt.value)" 
                                        variant="outline-primary"
                                        size="sm" 
                                        class="w-100"
                                        @click="selectRoomsForType(rt.value)">
                                        <b-icon icon="list-check" class="me-2"></b-icon>
                                        Choose Specific Rooms
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="text-center py-5">
                <b-icon icon="calendar-x" font-scale="3" class="text-muted mb-3"></b-icon>
                <p class="text-muted">Select hotel and dates to see available room types.</p>
            </div>
        </b-card>

        <!-- Step 3: Room Number Modal -->
        <b-modal v-model="showRoomModal" title="Select Room(s)" @ok="confirmRoomSelection" ok-title="Confirm" size="lg">
            <div>
                <div v-if="isLoadingRooms" class="text-center py-4">
                    <b-spinner label="Loading..." />
                </div>
                <div v-else-if="availableRoomsForSelectedType.length">
                    <b-row>
                        <b-col v-for="room in availableRoomsForSelectedType" :key="room.id" cols="12" sm="6" md="4"
                            lg="3" class="mb-3">
                            <div :class="[
                                'room-select-card',
                                selectedRoomNumbers.includes(room.id)
                                    ? 'selected'
                                    : '',
                            ]" @click="toggleRoomSelection(room.id)" style="cursor: pointer; position: relative">
                                <!-- Checkmark overlay for selected -->
                                <span v-if="selectedRoomNumbers.includes(room.id)" class="room-checkmark">
                                    <b-icon icon="check-circle-fill" variant="success" font-scale="1.5" />
                                </span>
                                <div class="d-flex align-items-center mb-2">
                                    <b-icon icon="door-open" class="me-2 text-primary" />
                                    <span class="fw-bold fs-5 text-dark">Room {{ room.room_number }}</span>
                                </div>
                                <div class="mb-1">
                                    <span class="badge px-3 py-1 rounded-pill bg-info text-white me-1 mb-1">
                                        {{ room.room_type?.name || 'Type' }}
                                    </span>
                                    <span v-if="room.floor"
                                        class="badge px-3 py-1 rounded-pill bg-secondary text-white me-1 mb-1">
                                        <b-icon icon="building" /> Floor {{ room.floor }}
                                    </span>
                                    <span v-if="room.size_sqft"
                                        class="badge px-3 py-1 rounded-pill bg-success text-white me-1 mb-1">
                                        <b-icon icon="aspect-ratio" /> {{ room.size_sqft }} sqft
                                    </span>
                                </div>
                                <div class="mb-1 text-muted" v-if="room.view">
                                    <b-icon icon="eye" /> {{ room.view }}
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </div>
                <div v-else class="text-danger">
                    No rooms available for this type and dates.
                </div>
            </div>
        </b-modal>

        <!-- Step 4: Guest Info Per Room, Auto by Capacity -->
        <b-card v-if="step >= 4" class="mb-4" header="3. Guest Information" header-bg-variant="light">
            <div v-if="errors.guestDetails" class="text-danger mb-3">
                {{ errors.guestDetails }}
            </div>
            <div v-for="roomTypeId in selectedRoomTypes" :key="roomTypeId">
                <h4 class="mt-3 mb-3 text-primary">
                    {{ getRoomTypeName(roomTypeId) }}
                    <span class="badge bg-secondary ms-2">
                        {{ roomSelections[roomTypeId] ? roomSelections[roomTypeId].length : 0 }} rooms
                    </span>
                </h4>

                <template v-if="guestDetails[roomTypeId] && guestDetails[roomTypeId].guests">
                    <div v-for="(roomGuest, idx) in guestDetails[roomTypeId].guests" :key="idx"
                        class="mb-4 p-3 border rounded bg-light" style="box-shadow: 0 2px 8px #0001">
                        <h5 class="mb-3">
                            <b-icon icon="door-open" class="me-2" />
                            Room {{ getRoomNumber(roomGuest.room_id) }}
                            <span class="badge bg-secondary ms-2">
                                Capacity: {{ getRoomCapacity(roomGuest.room_id) }}
                            </span>
                        </h5>

                        <div v-for="(guest, gIdx) in roomGuest.guests" :key="gIdx"
                            class="mb-3 p-3 bg-white rounded border" style="box-shadow: 0 1px 4px #0001">
                            <h6 class="mb-3 text-muted">Guest {{ gIdx + 1 }}</h6>
                            <b-row>
                                <b-col md="2">
                                    <b-form-group label="Passport Image*">
                                        <b-form-file v-model="guest.passport_image"
                                            :state="isValidPassportImageBase64(guest)"
                                            placeholder="Choose file..." browse-text="Choose File"
                                            @change="onPassportImageChange($event, roomTypeId, idx, gIdx)" />
                                        <b-form-invalid-feedback>
                                            Passport image is required
                                        </b-form-invalid-feedback>
                                        <div v-if="isProcessingOCR" class="mt-2">
                                            <small class="text-info">
                                                <b-spinner small></b-spinner> Processing OCR...
                                            </small>
                                        </div>
                                        <div v-if="guest.passport_image_preview" class="mt-2">
                                            <img :src="guest.passport_image_preview" alt="Preview"
                                                style="max-width: 100px; max-height: 80px; border-radius: 6px; border: 1px solid #eee;" />
                                        </div>
                                    </b-form-group>
                                </b-col>
                                <b-col md="3">
                                    <b-form-group label="Full Name*">
                                        <b-form-input v-model="guest.name" required 
                                            :state="guest.name && guest.name.trim() !== ''" />
                                        <b-form-invalid-feedback>
                                            Full name is required
                                        </b-form-invalid-feedback>
                                    </b-form-group>
                                </b-col>
                                <b-col md="2">
                                    <b-form-group label="DOB*">
                                        <b-form-datepicker v-model="guest.dob" placeholder="dd-mm-yyyy" 
                                            :state="guest.dob && guest.dob.trim() !== ''" />
                                        <b-form-invalid-feedback>
                                            Date of birth is required
                                        </b-form-invalid-feedback>
                                    </b-form-group>
                                </b-col>
                                <b-col md="2">
                                    <b-form-group label="Gender*">
                                        <b-form-select v-model="guest.gender" :options="genderOptions"
                                            placeholder="Select Gender" 
                                            :state="guest.gender && guest.gender.trim() !== ''" />
                                        <b-form-invalid-feedback>
                                            Gender is required
                                        </b-form-invalid-feedback>
                                    </b-form-group>
                                </b-col>
                                <b-col md="3">
                                    <b-form-group label="Passport ID*">
                                        <b-form-input v-model="guest.passport_id" 
                                            :state="guest.passport_id && guest.passport_id.trim() !== ''" />
                                        <b-form-invalid-feedback>
                                            Passport ID is required
                                        </b-form-invalid-feedback>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-button v-if="roomGuest.guests.length > 1" variant="danger" size="sm" class="mt-2"
                                @click="removeGuest(roomTypeId, idx, gIdx)">
                                <b-icon icon="trash" /> Remove Guest
                            </b-button>
                        </div>

                        <b-button v-if="roomGuest.guests.length < getRoomCapacity(roomGuest.room_id)" size="sm"
                            variant="outline-primary"
                            @click="addGuest(roomTypeId, idx, getRoomCapacity(roomGuest.room_id))">
                            + Add Guest
                        </b-button>
                    </div>
                </template>
            </div>
        </b-card>

        <!-- Step 5: Booking Summary -->
        <b-card v-if="step === 5" class="mb-4" header="4. Booking Summary" header-bg-variant="primary" header-text-variant="white">
            <div class="booking-summary">
                <!-- Hotel Information -->
                <div class="summary-section">
                    <div class="section-header">
                        <b-icon icon="building" class="me-2"></b-icon>
                        <h5 class="mb-0">Hotel Information</h5>
                    </div>
                    <div class="hotel-info-card">
                        <div class="hotel-name">{{ getHotelName(formFields.hotel_id) }}</div>
                        <div class="hotel-details">
                            <span class="badge bg-primary me-2">
                                <b-icon icon="calendar-check"></b-icon> 
                                {{ calculateDays(formFields.checkin_date, formFields.checkout_date) }} nights
                            </span>
                            <span class="badge bg-info me-2">
                                <b-icon icon="people"></b-icon> 
                                {{ formFields.num_of_adults }} adults, {{ formFields.num_of_child }} children
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Dates Information -->
                <div class="summary-section">
                    <div class="section-header">
                        <b-icon icon="calendar-event" class="me-2"></b-icon>
                        <h5 class="mb-0">Stay Dates</h5>
                    </div>
                    <div class="dates-card">
                        <div class="date-item">
                            <div class="date-label">Check-in</div>
                            <div class="date-value">{{ formatDisplayDate(formFields.checkin_date) }}</div>
                        </div>
                        <div class="date-arrow">
                            <b-icon icon="arrow-right"></b-icon>
                        </div>
                        <div class="date-item">
                            <div class="date-label">Check-out</div>
                            <div class="date-value">{{ formatDisplayDate(formFields.checkout_date) }}</div>
                        </div>
                    </div>
                </div>

                <!-- Room Types & Guests -->
                <div class="summary-section">
                    <div class="section-header">
                        <b-icon icon="door-open" class="me-2"></b-icon>
                        <h5 class="mb-0">Room Details</h5>
                    </div>
                    <div class="rooms-container">
                        <div v-for="roomTypeId in selectedRoomTypes" :key="roomTypeId" class="room-type-card">
                            <div class="room-type-header">
                                <h6 class="room-type-name">{{ getRoomTypeName(roomTypeId) }}</h6>
                                <span class="room-count">{{ roomSelections[roomTypeId] ? roomSelections[roomTypeId].length : 0 }} room(s)</span>
                            </div>
                            <div class="rooms-list">
                                <div v-for="room in roomSelections[roomTypeId]" :key="room.id" class="room-item">
                                    <div class="room-number">
                                        <b-icon icon="door-open"></b-icon>
                                        Room {{ room.room_number }}
                                    </div>
                                    <div class="guests-list">
                                        <div v-for="(guest, gIdx) in getGuestsForRoom(roomTypeId, room.id)" :key="gIdx" class="guest-item">
                                            <b-icon icon="person-circle"></b-icon>
                                            {{ guest.name }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Special Requests -->
                <div v-if="formFields.special_request" class="summary-section">
                    <div class="section-header">
                        <b-icon icon="chat-text" class="me-2"></b-icon>
                        <h5 class="mb-0">Special Requests</h5>
                    </div>
                    <div class="special-requests-card">
                        <p class="mb-0">{{ formFields.special_request }}</p>
                    </div>
                </div>

                <!-- Pricing Information -->
                <div class="summary-section">
                    <div class="section-header">
                        <b-icon icon="currency-dollar" class="me-2"></b-icon>
                        <h5 class="mb-0">Pricing Details</h5>
                    </div>
                    <div class="pricing-card">
                        <div class="pricing-breakdown">
                            <div v-for="roomTypeId in selectedRoomTypes" :key="roomTypeId" class="pricing-item">
                                <div class="pricing-header">
                                    <span class="room-type-name">{{ getRoomTypeName(roomTypeId) }}</span>
                                    <span class="room-count">{{ roomSelections[roomTypeId] ? roomSelections[roomTypeId].length : 0 }} room(s)</span>
                                </div>
                                <div class="pricing-details">
                                    <div class="price-per-night">
                                        <span class="price-label">Price per night:</span>
                                        <span class="price-value">{{ formatCurrency(getRoomTypePrice(roomTypeId)) }}</span>
                                    </div>
                                    <div class="total-nights">
                                        <span class="price-label">Nights:</span>
                                        <span class="price-value">{{ calculateDays(formFields.checkin_date, formFields.checkout_date) }}</span>
                                    </div>
                                    <div class="subtotal">
                                        <span class="price-label">Subtotal:</span>
                                        <span class="price-value">{{ formatCurrency(calculateRoomTypeTotal(roomTypeId)) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="pricing-total">
                            <div class="total-line">
                                <span class="total-label">Total Amount:</span>
                                <span class="total-value">{{ formatCurrency(calculateTotalPrice()) }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Contact Information -->
                <div class="summary-section">
                    <div class="section-header">
                        <b-icon icon="person" class="me-2"></b-icon>
                        <h5 class="mb-0">Contact Information</h5>
                    </div>
                    <div class="contact-card">
                        <div class="contact-item">
                            <b-icon icon="person"></b-icon>
                            <span>{{ formFields.first_name }} {{ formFields.last_name }}</span>
                        </div>
                        <div class="contact-item">
                            <b-icon icon="envelope"></b-icon>
                            <span>{{ formFields.email }}</span>
                        </div>
                        <div class="contact-item">
                            <b-icon icon="telephone"></b-icon>
                            <span>{{ formFields.phone }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </b-card>

        <!-- Action Buttons -->
        <div class="d-flex gap-2 mt-4">
            <b-button v-if="step < 5" variant="primary" @click="nextStep" :disabled="!canProceed">
                Next
            </b-button>
            <b-button v-if="step > 1" variant="secondary" @click="prevStep">
                Back
            </b-button>
            <b-button v-if="step === 5" type="submit" variant="success" :disabled="isSubmitting" @click="submitBooking">
                <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
                Confirm Booking
            </b-button>
            <b-button variant="danger" @click="cancel">Cancel</b-button>
        </div>
    </div>
</template>

<script>
import { request } from "@/Util/Request";
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";
export default {
    name: "AddBooking",
    components: {
        VueTelInput,
        FlatPickr
    },
    data() {
        return {
            step: 1,
            formFields: {
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                hotel_id: null,
                checkin_date: '',
                checkout_date: '',
                num_of_adults: 1,
                num_of_child: 0,
                special_request: '',
                payment_method: 'cash',
                room_types: []
            },
            dateRange: [], // Will be [checkin, checkout]
            phoneValid: false,
            phoneObject: null,
            errors: {}, // Add errors object
            validationRules: {
                first_name: { required: true, minLength: 2 },
                last_name: { required: true, minLength: 2 },
                email: { required: true, email: true },
                phone: { required: true, minLength: 8 },
                hotel_id: { required: true },
                num_of_adults: { required: true, min: 1 },
                num_of_child: { min: 0 },
                dateRange: { required: true },
                roomTypes: { required: true },
                guestDetails: { required: true }
            },
            dateOptions: {
                mode: 'range',
                dateFormat: 'Y-m-d',
                altInput: true,
                altFormat: 'd M Y',
                allowInput: true,
                showMonths: 2,
                minDate: 'today',
                wrap: true,
                onReady(selectedDates, dateStr, instance) {
                    if (instance.altInput) {
                        instance.altInput.setAttribute('readonly', 'readonly');
                    }
                },
                onOpen(selectedDates, dateStr, instance) {
                    if (instance.altInput) {
                        instance.altInput.setAttribute('readonly', 'readonly');
                    }
                },
                onChange: (selectedDates, dateStr) => {
                    console.log('Flatpickr onChange:', selectedDates, dateStr);
                }
            },
            paymentOptions: [
                { value: 'cash', text: 'Cash' },
                { value: 'credit_card', text: 'Credit Card' },
                { value: 'bank_transfer', text: 'Bank Transfer' }
            ],
            isSubmitting: false,
            hotelOptions: [],
            roomTypeOptions: [],
            platforms: [
                { id: "EXPEDIA", label: "EXPEDIA" },
                { id: "BOOKING.COM", label: "BOOKING.COM" },
                { id: "AIRBNB", label: "AIRBNB" },
                { id: "MANUAL", label: "MANUAL" },
            ],
            genderOptions: [
                { value: "", text: "Select Gender*" },
                { value: "male", text: "Male" },
                { value: "female", text: "Female" },
                { value: "other", text: "Other" },
            ],
            selectedRoomTypeId: null,
            showRoomModal: false,
            availableRoomsForSelectedType: [],
            selectedRoomNumbers: [],
            selectedRooms: [],
            selectedRoomTypes: [], // Array to track selected room types
            roomSelections: {},
            guestDetails: [],
            isLoadingRooms: false,
            isProcessingOCR: false,
        };
    },
    mounted() {
        console.log('Component mounted, formFields:', this.formFields);
        console.log('First name:', this.formFields.first_name);
        this.fetchHotels();
    },
    watch: {
        "formFields.hotel_id": "fetchRoomTypes",
        "formFields.checkin_date": "fetchRoomTypes",
        "formFields.checkout_date": "fetchRoomTypes",
        dateRange(newVal) {
            console.log('dateRange changed:', newVal, typeof newVal);
            if (Array.isArray(newVal) && newVal.length === 2) {
                // Handle array of Date objects
                this.formFields.checkin_date = this.formatDate(newVal[0]);
                this.formFields.checkout_date = this.formatDate(newVal[1]);
                // Clear date error if dates are selected
                if (this.errors.dateRange) {
                    delete this.errors.dateRange;
                }
            } else if (typeof newVal === 'string' && newVal.includes(' to ')) {
                // Handle string format
                const [checkin, checkout] = newVal.split(' to ');
                this.formFields.checkin_date = checkin;
                this.formFields.checkout_date = checkout;
                // Clear date error if dates are selected
                if (this.errors.dateRange) {
                    delete this.errors.dateRange;
                }
            }
        }
    },
    computed: {
        canProceed() {
            if (this.step === 1) {
                return this.validateStep1();
            }
            if (this.step === 2) {
                return this.validateStep2();
            }
            if (this.step === 4) {
                return this.validateGuestDetails();
            }
            return true;
        },
    },
    methods: {
        formatDate(date) {
            return new Date(date).toISOString().split('T')[0];
        },

        validateField(field) {
            console.log(`Validating field: ${field}`);
            
            if (!this.formFields) {
                console.error('formFields is not defined');
                return null;
            }

            const value = field === 'dateRange'
                ? this.dateRange
                : (this.formFields[field] !== undefined ? this.formFields[field] : null);

            const rules = this.validationRules[field];

            if (!rules) return null;

            // Clear previous error
            if (this.errors[field]) {
                delete this.errors[field];
            }

            // Required validation
            if (rules.required) {
                if (field === 'dateRange') {
                    // Check if dateRange is valid (either array with 2 dates or string with both dates)
                    if (!value) {
                        this.errors[field] = 'Please select both check-in and check-out dates';
                        return false;
                    }
                    
                    if (Array.isArray(value)) {
                        if (value.length !== 2 || !value[0] || !value[1]) {
                            this.errors[field] = 'Please select both check-in and check-out dates';
                            return false;
                        }
                    } else if (typeof value === 'string') {
                        if (!value.includes(' to ') || value.split(' to ').length !== 2) {
                            this.errors[field] = 'Please select both check-in and check-out dates';
                            return false;
                        }
                    } else {
                        this.errors[field] = 'Please select both check-in and check-out dates';
                        return false;
                    }
                } else if (value === null || value === undefined || value === '') {
                    this.errors[field] = `${this.getFieldLabel(field)} is required`;
                    return false;
                }
            }

            // Min length validation
            if (rules.minLength && value && value.length < rules.minLength) {
                this.errors[field] = `${this.getFieldLabel(field)} must be at least ${rules.minLength} characters`;
                return false;
            }

            // Min value validation
            if (rules.min !== undefined && value < rules.min) {
                this.errors[field] = `${this.getFieldLabel(field)} must be at least ${rules.min}`;
                return false;
            }

            // Email validation
            if (field === 'email' && value && !this.validateEmail(value)) {
                this.errors.email = 'Please enter a valid email address';
                return false;
            }

            // Phone validation
            if (field === 'phone' && rules.required && !this.formFields.phone) {
                this.errors.phone = 'Phone number is required';
                return false;
            } else if (field === 'phone' && this.formFields.phone && !this.phoneValid) {
                this.errors.phone = 'Please enter a valid phone number';
                return false;
            }

            return true; // Return true if validation passes
        },
        validateStep1() {
            let isValid = true;

            // Validate all step 1 fields
            ['first_name', 'last_name', 'email', 'phone', 'hotel_id', 'num_of_adults', 'dateRange'].forEach(field => {
                const result = this.validateField(field);
                if (result === false) {
                    isValid = false;
                }
            });

            return isValid;
        },
        validateStep2() {
            // Clear previous error
            if (this.errors.roomTypes) {
                delete this.errors.roomTypes;
            }
            
            // Check if at least one room type is selected
            if (this.selectedRoomTypes.length === 0) {
                this.errors.roomTypes = 'Please select at least one room type';
                return false;
            }
            
            // For Step 2, we only validate that room types are selected
            // Room selection validation will happen when user tries to proceed
            return true;
        },

        validateGuestDetails() {
            let isValid = true;
            this.errors.guestDetails = '';

            // Check if any room types are selected
            if (this.selectedRoomTypes.length === 0) {
                this.errors.guestDetails = 'Please select at least one room type';
                return false;
            }

            // Validate each guest's information
            for (const roomTypeId of this.selectedRoomTypes) {
                const roomGuests = this.guestDetails[roomTypeId]?.guests || [];

                for (const roomGuest of roomGuests) {
                    for (const guest of roomGuest.guests) {
                        // Required field validations
                        if (!guest.name || guest.name.trim() === '') {
                            this.errors.guestDetails = 'Please fill all guest names';
                            isValid = false;
                            break;
                        }

                        if (!guest.dob || guest.dob.trim() === '') {
                            this.errors.guestDetails = 'Please fill date of birth for all guests';
                            isValid = false;
                            break;
                        }

                        if (!guest.gender || guest.gender.trim() === '') {
                            this.errors.guestDetails = 'Please select gender for all guests';
                            isValid = false;
                            break;
                        }

                        if (!guest.passport_id || guest.passport_id.trim() === '') {
                            this.errors.guestDetails = 'Please fill passport ID for all guests';
                            isValid = false;
                            break;
                        }

                        if (!guest.passport_image_base64) {
                            this.errors.guestDetails = 'Please upload passport image for all guests';
                            isValid = false;
                            break;
                        }

                        // Date validation
                        if (guest.dob && new Date(guest.dob) > new Date()) {
                            this.errors.guestDetails = 'Date of birth cannot be in the future';
                            isValid = false;
                            break;
                        }
                    }
                    if (!isValid) break;
                }
                if (!isValid) break;
            }

            return isValid;
        },
        resetError() {
            this.formErrors = new Error({});
        },
        getFieldLabel(field) {
            const labels = {
                first_name: 'First name',
                last_name: 'Last name',
                email: 'Email',
                phone: 'Phone',
                hotel_id: 'Hotel',
                num_of_adults: 'Number of adults',
                num_of_child: 'Number of children',
                dateRange: 'Stay dates'
            };
            return labels[field] || field;
        },

        validateEmail(email) {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(email);
        },

        handlePhoneInput(number, phoneObject) {
            this.phoneObject = phoneObject;
            this.formFields.phone = phoneObject.nationalNumber;
        },

        handlePhoneValidate(phoneObject) {
            this.phoneValid = phoneObject.valid;
            this.validateField('phone');
        },
        getRoomCapacity(roomId) {
            // Find room capacity across all room types
            for (const roomTypeId in this.roomSelections) {
                const room = this.roomSelections[roomTypeId].find(r => r.id === roomId);
                if (room) return room.room_type?.capacity || 1;
            }
            return 1; // Default capacity if not found
        },

        getRoomNumber(roomId) {
            // Find room number across all room types
            for (const roomTypeId in this.roomSelections) {
                const room = this.roomSelections[roomTypeId].find(r => r.id === roomId);
                if (room) return room.room_number;
            }
            return "";
        },

        toggleRoomTypeSelection(roomTypeId) {
            const index = this.selectedRoomTypes.indexOf(roomTypeId);
            if (index === -1) {
                this.selectedRoomTypes.push(roomTypeId);
                // Initialize room selections and guest details for this room type
                this.$set(this.roomSelections, roomTypeId, []);
                this.$set(this.guestDetails, roomTypeId, {
                    rooms: [],
                    guests: []
                });
            } else {
                this.selectedRoomTypes.splice(index, 1);
                this.$delete(this.roomSelections, roomTypeId);
                this.$delete(this.guestDetails, roomTypeId);
            }
        },
        selectRoomsForType(roomTypeId) {
            this.selectedRoomTypeId = roomTypeId; // Track which type we're selecting rooms for
            this.showRoomModal = true;
            this.fetchAvailableRoomsForType(roomTypeId);
            
            // Pre-populate selectedRoomNumbers with already selected rooms for this type
            this.selectedRoomNumbers = [];
            if (this.roomSelections[roomTypeId] && this.roomSelections[roomTypeId].length > 0) {
                this.selectedRoomNumbers = this.roomSelections[roomTypeId].map(room => room.id);
            }
        },
        toggleRoomSelection(roomId) {
            const idx = this.selectedRoomNumbers.indexOf(roomId);
            if (idx === -1) {
                this.selectedRoomNumbers.push(roomId);
            } else {
                this.selectedRoomNumbers.splice(idx, 1);
            }
        },
        nextStep() {
            if (this.step === 1 && !this.validateStep1()) {
                this.$bvToast.toast('Please fix all validation errors', {
                    title: 'Validation Error',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            if (this.step === 2) {
                // Check if at least one room type is selected
                if (this.selectedRoomTypes.length === 0) {
                    this.errors.roomTypes = 'Please select at least one room type';
                    this.$bvToast.toast('Please select at least one room type', {
                        title: 'Validation Error',
                        variant: 'danger',
                        solid: true
                    });
                    return;
                }
                // Check if all selected room types have rooms selected
                const allHaveRooms = this.selectedRoomTypes.every(rtId => 
                    this.roomSelections[rtId] && this.roomSelections[rtId].length > 0
                );
                if (!allHaveRooms) {
                    this.errors.roomTypes = 'Please select rooms for all selected room types';
                    this.$bvToast.toast('Please select rooms for all selected room types', {
                        title: 'Validation Error',
                        variant: 'danger',
                        solid: true
                    });
                    return;
                }
                // Clear the error if validation passes
                if (this.errors.roomTypes) {
                    delete this.errors.roomTypes;
                }
            }

            if (this.step === 4 && !this.validateGuestDetails()) {
                this.$bvToast.toast('Please complete all guest information', {
                    title: 'Validation Error',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            if (this.step < 5) {
                this.step++;
            }
        },
        prevStep() {
            if (this.step > 1) {
                this.step--;
            }
        },
        onHotelChange() {
            this.selectedRoomTypeId = null;
            this.selectedRoomNumbers = [];
            this.selectedRooms = [];
            this.guestDetails = [];
            this.roomTypeOptions = [];
            this.fetchRoomTypes();
        },
        onDateChange() {
            this.selectedRoomTypeId = null;
            this.selectedRoomNumbers = [];
            this.selectedRooms = [];
            this.guestDetails = [];
            this.roomTypeOptions = [];
            this.fetchRoomTypes();
        },
        async fetchHotels() {
            try {
                const response = await request({
                    method: "get",
                    url: "/hotels",
                });
                this.hotelOptions = (response.data || []).map((hotel) => ({
                    value: hotel.id,
                    text: hotel.name,
                }));
            } catch (e) {
                this.hotelOptions = [];
            }
        },
        async fetchRoomTypes() {
            if (
                !this.formFields.hotel_id ||
                !this.formFields.checkin_date ||
                !this.formFields.checkout_date
            ) {
                this.roomTypeOptions = [];
                return;
            }
            try {
                const response = await request({
                    method: "get",
                    url: `/public/available-room-types`,
                    params: {
                        hotel_id: this.formFields.hotel_id,
                        check_in: this.formFields.checkin_date,
                        check_out: this.formFields.checkout_date,
                        adults: this.formFields.num_of_adults,
                        children: this.formFields.num_of_child,
                    },
                });
                this.roomTypeOptions = (response.data || []).map((rt) => ({
                    value: rt.id,
                    text: rt.name,
                    image: rt.image,
                    capacity: rt.capacity,
                    price: rt.base_price,
                    available_rooms: rt.available_rooms,
                    attachments: rt.attachments || [],
                    bed_type: rt.bed_type,
                    description: rt.description,
                }));
            } catch (e) {
                this.roomTypeOptions = [];
            }
            // this.selectedRoomTypeId = null;
            // this.selectedRoomNumbers = [];
            // this.selectedRooms = [];
            // this.guestDetails = [];
        },
        async selectRoomType(roomTypeId) {
            this.selectedRoomTypeId = roomTypeId;
            // this.selectedRoomNumbers = [];
            // this.selectedRooms = [];
            // this.guestDetails = [];
            this.showRoomModal = true;
            this.fetchAvailableRoomsForType(roomTypeId);
        },
        async fetchAvailableRoomsForType(roomTypeId) {
            this.isLoadingRooms = true;
            try {
                const response = await request({
                    method: "get",
                    url: "/rooms/available",
                    params: {
                        room_type_id: roomTypeId,
                        check_in: this.formFields.checkin_date,
                        check_out: this.formFields.checkout_date,
                    },
                });
                this.availableRoomsForSelectedType =
                    response.rooms || response.data || [];
            } catch (e) {
                this.availableRoomsForSelectedType = [];
            }
            this.isLoadingRooms = false;
        },
        confirmRoomSelection() {
            this.showRoomModal = false;
            
            if (this.selectedRoomNumbers.length) {
                const selectedRooms = this.availableRoomsForSelectedType.filter(
                    room => this.selectedRoomNumbers.includes(room.id)
                );

                // Update room selections for this room type
                this.$set(this.roomSelections, this.selectedRoomTypeId, selectedRooms);

                // Initialize guest details for each room
                const guestDetailsForType = {
                    rooms: selectedRooms,
                    guests: selectedRooms.map(room => ({
                        room_id: room.id,
                        guests: [{
                            name: "",
                            email: "",
                            phone: "",
                            dob: "",
                            gender: "",
                            passport_id: "",
                            passport_image: null,
                            passport_image_preview: null,
                            passport_image_base64: null,
                        }]
                    }))
                };

                this.$set(this.guestDetails, this.selectedRoomTypeId, guestDetailsForType);
            } else {
                // No rooms selected - remove this room type from selections
                this.$delete(this.roomSelections, this.selectedRoomTypeId);
                this.$delete(this.guestDetails, this.selectedRoomTypeId);
                
                // If no room types have rooms selected, go back to step 2
                const hasAnyRooms = this.selectedRoomTypes.some(rtId => 
                    this.roomSelections[rtId] && this.roomSelections[rtId].length > 0
                );
                
                if (!hasAnyRooms && this.step > 2) {
                    this.step = 2;
                    this.$bvToast.toast('Please select rooms for at least one room type', {
                        title: 'Room Selection Required',
                        variant: 'warning',
                        solid: true
                    });
                }
            }
            
            this.selectedRoomNumbers = []; // Reset for next selection

            // Automatically advance to guest details if all room types have rooms selected
            if (this.selectedRoomTypes.every(rtId =>
                this.roomSelections[rtId] && this.roomSelections[rtId].length > 0)) {
                this.step = 4;
            }
        },
        addGuest(roomTypeId, roomIdx, capacity) {
            // Check if we can add more guests (current guests < room capacity)
            const currentGuests = this.guestDetails[roomTypeId].guests[roomIdx].guests.length;

            if (currentGuests < capacity) {
                this.guestDetails[roomTypeId].guests[roomIdx].guests.push({
                    name: "",
                    email: "",
                    phone: "",
                    dob: "",
                    gender: "",
                    passport_id: "",
                    passport_image: null,
                    passport_image_preview: null,
                    passport_image_base64: null
                });
            }
        },
        removeGuest(roomTypeId, roomIdx, guestIdx) {
            // Make sure we have valid references
            if (!this.guestDetails[roomTypeId] ||
                !this.guestDetails[roomTypeId].guests[roomIdx] ||
                !this.guestDetails[roomTypeId].guests[roomIdx].guests) {
                console.error('Invalid guest details structure');
                return;
            }

            // Only remove if there's more than 1 guest (keep at least 1)
            if (this.guestDetails[roomTypeId].guests[roomIdx].guests.length > 1) {
                // Use Vue.set or this.$delete to ensure reactivity
                this.$delete(
                    this.guestDetails[roomTypeId].guests[roomIdx].guests,
                    guestIdx
                );
            } else {
                console.log('Cannot remove the last guest');
            }
        },
        getHotelName(hotelId) {
            const h = this.hotelOptions.find((h) => h.value === hotelId);
            return h ? h.text : "";
        },
        getRoomTypeName(roomTypeId) {
            const rt = this.roomTypeOptions.find(
                (rt) => rt.value === roomTypeId
            );
            return rt ? rt.text : "";
        },
        getRoomTypeImage(roomType) {
            // First check if there's a direct image property
            if (roomType.image) {
                return roomType.image;
            }
            
            // Check if there are attachments with images
            if (roomType.attachments && roomType.attachments.length > 0) {
                // Find the first image attachment
                const imageAttachment = roomType.attachments.find(att => 
                    att.path && (att.path.includes('.jpg') || att.path.includes('.jpeg') || att.path.includes('.png') || att.path.includes('.webp'))
                );
                if (imageAttachment) {
                    return imageAttachment.path;
                }
            }
            
            return null;
        },
        getPlatformLabel(platformId) {
            const p = this.platforms.find((p) => p.id === platformId);
            return p ? p.label : "";
        },
        getGuestsForRoom(roomTypeId, roomId) {
            const roomGuests = this.guestDetails[roomTypeId]?.guests || [];
            const roomGuest = roomGuests.find(g => g.room_id === roomId);
            return roomGuest ? roomGuest.guests : [];
        },
        calculateDays(checkin, checkout) {
            if (!checkin || !checkout) return 0;
            const start = new Date(checkin);
            const end = new Date(checkout);
            const diffTime = Math.abs(end - start);
            return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        },
        formatDisplayDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        },
        getRoomTypePrice(roomTypeId) {
            const roomType = this.roomTypeOptions.find(rt => rt.value === roomTypeId);
            return roomType ? roomType.price : 0;
        },
        calculateRoomTypeTotal(roomTypeId) {
            const pricePerNight = this.getRoomTypePrice(roomTypeId);
            const numRooms = this.roomSelections[roomTypeId] ? this.roomSelections[roomTypeId].length : 0;
            const numNights = this.calculateDays(this.formFields.checkin_date, this.formFields.checkout_date);
            return pricePerNight * numRooms * numNights;
        },
        calculateTotalPrice() {
            return this.selectedRoomTypes.reduce((total, roomTypeId) => {
                return total + this.calculateRoomTypeTotal(roomTypeId);
            }, 0);
        },
        formatCurrency(amount) {
            return new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
                minimumFractionDigits: 2
            }).format(amount);
        },
        isValidPassportImage(image) {
            return !image || (typeof File !== 'undefined' && image instanceof File);
        },
        isValidPassportImageBase64(guest) {
            return guest.passport_image_base64 && guest.passport_image_base64.trim() !== '';
        },
        async submitBooking() {
            if (!this.validateStep1() || !this.validateGuestDetails()) {
                this.$bvToast.toast('Please fix all validation errors', {
                    title: 'Validation Error',
                    variant: 'danger',
                    solid: true
                });
                return;
            }
            this.isSubmitting = true;

            // Prepare the room_types data structure
            const roomTypesPayload = this.selectedRoomTypes.map(roomTypeId => {
                const rooms = this.roomSelections[roomTypeId] || [];
                const roomGuests = this.guestDetails[roomTypeId]?.guests || [];

                // Map guests to their respective rooms
                const guestsByRoom = rooms.map((room) => {
                    // Find guests for this specific room
                    const guestsForRoom = roomGuests.find(g => g.room_id === room.id)?.guests || [];

                    return guestsForRoom.map(guest => ({
                        name: guest.name,
                        dob: guest.dob,
                        gender: guest.gender,
                        passport_id: guest.passport_id,
                        passport_image_base64: guest.passport_image_base64 || null
                    }));
                }).flat(); // Flatten the array of arrays

                return {
                    room_type_id: roomTypeId,
                    rooms: rooms.map(r => r.id),
                    guests: guestsByRoom
                };
            });

            const payload = {
                ...this.formFields,
                room_types: roomTypesPayload
            };
            console.log('Booking Payload:', payload);

            try {
                await request({
                    method: 'post',
                    url: '/booking/manual',
                    data: payload
                });
                // const response = await this.$http.post('/api/bookings', payload);
                this.$router.push('/admin/booking');
            } catch (error) {
                console.error('Booking failed:', error);
                this.$bvToast.toast('Booking failed. Please try again.', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.isSubmitting = false;
            }
        },
        cancel() {
            this.$router.back();
        },
        resetForm() {
            this.formFields = {
                hotel_id: null,
                first_name: "",
                last_name: "",
                guest_email: "",
                guest_phone: "",
                checkin_date: "",
                checkout_date: "",
                num_of_adults: 1,
                num_of_child: 0,
                special_requests: "",
                platform: null,
            };
            this.roomTypeOptions = [];
            this.selectedRoomTypeId = null;
            this.selectedRoomNumbers = [];
            this.selectedRooms = [];
            this.guestDetails = [];
            this.step = 1;
        },
        async onPassportImageChange(event, roomTypeId, roomIdx, guestIdx) {
            const file = event.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = e => {
                    this.$set(this.guestDetails[roomTypeId].guests[roomIdx].guests[guestIdx], 'passport_image_preview', e.target.result);
                    this.guestDetails[roomTypeId].guests[roomIdx].guests[guestIdx].passport_image = file;
                    // Store base64 data for API
                    this.guestDetails[roomTypeId].guests[roomIdx].guests[guestIdx].passport_image_base64 = e.target.result;
                };
                reader.readAsDataURL(file);

                // OCR Processing
                await this.processPassportOCR(file, roomTypeId, roomIdx, guestIdx);
            } else {
                this.$set(this.guestDetails[roomTypeId].guests[roomIdx].guests[guestIdx], 'passport_image_preview', null);
                this.guestDetails[roomTypeId].guests[roomIdx].guests[guestIdx].passport_image = null;
                this.guestDetails[roomTypeId].guests[roomIdx].guests[guestIdx].passport_image_base64 = null;
            }
        },
        async processPassportOCR(file, roomTypeId, roomIdx, guestIdx) {
            this.isProcessingOCR = true;
            const formData = new FormData();
            formData.append('image', file);
            
            try {
                const response = await request({
                    method: 'post',
                    url: '/ocr/extract-text',
                    data: formData,
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                
                if (response.success && response.data) {
                    const guest = this.guestDetails[roomTypeId].guests[roomIdx].guests[guestIdx];
                    
                    // Auto-fill extracted data
                    if (response.data.passport_number) {
                        this.$set(guest, 'passport_id', response.data.passport_number);
                    }
                    if (response.data.given_names) {
                        this.$set(guest, 'name', response.data.given_names);
                    }
                    if (response.data.sex) {
                        this.$set(guest, 'gender', response.data.sex.toLowerCase());
                    }
                    if (response.data.date_of_birth) {
                        this.$set(guest, 'dob', response.data.date_of_birth);
                    }

                    // Show success message
                    this.$bvToast.toast('Passport details extracted successfully!', {
                        title: 'OCR Success',
                        variant: 'success',
                        solid: true
                    });
                }
            } catch (error) {
                console.error('OCR extraction failed:', error);
                this.$bvToast.toast('OCR extraction failed. Please fill the details manually.', {
                    title: 'OCR Error',
                    variant: 'warning',
                    solid: true
                });
            } finally {
                this.isProcessingOCR = false;
            }
        },
    },
    filters: {
        currency(value) {
            if (!value) return "";
            return "₹" + parseFloat(value).toFixed(2);
        },
    },
};
</script>

<style scoped>
.admin-booking-form {
    padding: 20px;
    /* max-width: 900px; */
    margin: 0 auto;
}

.room-type-card {
    border-radius: 14px;
    transition: box-shadow 0.2s, border-color 0.2s;
    cursor: pointer;
}

.room-type-card:hover,
.room-type-card.border-primary {
    box-shadow: 0 0 0 2px #0d6efd33, 0 2px 8px #e3e3e3;
    border-color: #0d6efd !important;
}

/* Enhanced Room Type Cards */
.enhanced-room-type-card {
    background: white;
    border: 2px solid #e9ecef;
    border-radius: 16px;
    overflow: hidden;
    transition: all 0.3s ease;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
}

.enhanced-room-type-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: #007bff;
}

.enhanced-room-type-card.selected {
    border-color: #28a745;
    box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.2), 0 8px 25px rgba(0, 0, 0, 0.15);
    background: linear-gradient(135deg, #f8fff9 0%, #ffffff 100%);
}

.room-type-image {
    position: relative;
    overflow: hidden;
    height: 200px;
}

.room-type-main-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transition: transform 0.3s ease;
}

.enhanced-room-type-card:hover .room-type-main-image {
    transform: scale(1.05);
}

.room-type-overlay {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 2;
}

.price-badge {
    background: rgba(40, 167, 69, 0.95);
    color: white;
    padding: 8px 12px;
    border-radius: 20px;
    text-align: center;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.price-amount {
    display: block;
    font-size: 1.1rem;
    font-weight: 700;
    line-height: 1;
}

.price-period {
    display: block;
    font-size: 0.75rem;
    opacity: 0.9;
    margin-top: 2px;
}

.room-type-placeholder {
    height: 100%;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px 16px 0 0;
}

.room-type-content {
    padding: 20px;
    flex: 1;
    display: flex;
    flex-direction: column;
}

.room-type-header {
    margin-bottom: 15px;
}

.room-type-title {
    font-size: 1.2rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 8px;
    line-height: 1.3;
}

.room-type-meta {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.capacity-badge,
.availability-badge {
    background: #f8f9fa;
    color: #495057;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    border: 1px solid #e9ecef;
}

.capacity-badge .b-icon {
    color: #007bff;
}

.availability-badge .b-icon {
    color: #28a745;
}

.room-type-details {
    margin-bottom: 20px;
    flex: 1;
}

.detail-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 8px;
    font-size: 0.9rem;
    color: #495057;
    line-height: 1.4;
}

.detail-item:last-child {
    margin-bottom: 0;
}

.detail-item .b-icon {
    margin-top: 2px;
    flex-shrink: 0;
}

.description-text {
    font-size: 0.85rem;
    color: #6c757d;
    line-height: 1.5;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #f1f3f4;
}

.description-text span {
    display: block;
    margin-top: 4px;
}

.room-type-actions {
    margin-top: auto;
}

.room-type-actions .btn {
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.2s ease;
    padding: 8px 16px;
}

.room-type-actions .btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.room-type-actions .btn-success {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    border: none;
}

.room-type-actions .btn-primary {
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
    border: none;
}

/* Modal border and radius */
.b-modal .modal-content {
    border-radius: 12px !important;
    border: 1.5px solid #e9ecef !important;
    box-shadow: 0 4px 24px #0002 !important;
}

.room-type-img {
    width: 100%;
    height: 120px;
    object-fit: cover;
    border-radius: 12px 12px 0 0;
}

.room-pill {
    margin: 0.25rem 0.5rem 0.25rem 0;
    display: inline-block;
}

.room-select-card {
    background: #fff;
    border: 2px solid #e3e3e3;
    border-radius: 12px;
    padding: 18px 14px;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-shadow: 0 1px 4px #0001;
    min-height: 140px;
    position: relative;
}

.room-select-card.selected {
    border-color: #0d6efd;
    box-shadow: 0 0 0 2px #0d6efd33, 0 2px 8px #e3e3e3;
    background: #f0f8ff;
}

.room-select-card:hover {
    border-color: #0d6efd;
}

.room-checkmark {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
}

/* Booking Summary Styles */
.booking-summary {
    padding: 20px 0;
}

.summary-section {
    margin-bottom: 30px;
}

.summary-section:last-child {
    margin-bottom: 0;
}

.section-header {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e9ecef;
}

.section-header h5 {
    color: #495057;
    font-weight: 600;
    margin: 0;
}

.hotel-info-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.hotel-name {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 10px;
}

.hotel-details {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.hotel-details .badge {
    font-size: 0.85rem;
    padding: 6px 12px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.2) !important;
    color: white !important;
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.dates-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f8f9fa;
    padding: 20px;
    border-radius: 12px;
    border: 1px solid #e9ecef;
}

.date-item {
    text-align: center;
    flex: 1;
}

.date-label {
    font-size: 0.875rem;
    color: #6c757d;
    font-weight: 500;
    margin-bottom: 5px;
}

.date-value {
    font-size: 1.1rem;
    font-weight: 600;
    color: #495057;
}

.date-arrow {
    color: #6c757d;
    font-size: 1.2rem;
    margin: 0 20px;
}

.rooms-container {
    display: grid;
    gap: 15px;
}

.room-type-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
}

.room-type-card:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}

.room-type-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #f1f3f4;
}

.room-type-name {
    color: #495057;
    font-weight: 600;
    margin: 0;
}

.room-count {
    background: #e3f2fd;
    color: #1976d2;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 500;
}

.rooms-list {
    display: grid;
    gap: 12px;
}

.room-item {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 15px;
    border-left: 4px solid #007bff;
}

.room-number {
    font-weight: 600;
    color: #495057;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.guests-list {
    display: grid;
    gap: 6px;
}

.guest-item {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #6c757d;
    font-size: 0.9rem;
}

.special-requests-card {
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 8px;
    padding: 15px;
    color: #856404;
}

.contact-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.contact-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid #f1f3f4;
}

.contact-item:last-child {
    border-bottom: none;
}

.contact-item .b-icon {
    color: #007bff;
    width: 20px;
}

/* Pricing Styles */
.pricing-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.pricing-breakdown {
    display: grid;
    gap: 15px;
    margin-bottom: 20px;
}

.pricing-item {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 15px;
    border-left: 4px solid #28a745;
}

.pricing-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #dee2e6;
}

.pricing-header .room-type-name {
    font-weight: 600;
    color: #495057;
}

.pricing-header .room-count {
    background: #d4edda;
    color: #155724;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 500;
}

.pricing-details {
    display: grid;
    gap: 8px;
}

.price-per-night,
.total-nights,
.subtotal {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
}

.price-label {
    color: #6c757d;
    font-size: 0.9rem;
}

.price-value {
    font-weight: 600;
    color: #495057;
}

.subtotal .price-value {
    color: #28a745;
    font-weight: 700;
}

.pricing-total {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    color: white;
    padding: 20px;
    border-radius: 12px;
    margin-top: 15px;
}

.total-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1.1rem;
    font-weight: 700;
}

.total-label {
    color: rgba(255, 255, 255, 0.9);
}

.total-value {
    font-size: 1.3rem;
    font-weight: 800;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Responsive Design */
@media (max-width: 768px) {
    .dates-card {
        flex-direction: column;
        gap: 15px;
    }
    
    .date-arrow {
        transform: rotate(90deg);
        margin: 10px 0;
    }
    
    .hotel-details {
        flex-direction: column;
        align-items: flex-start;
    }
    
    .room-type-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
}
</style>
